import { ArrowRightAlt, ExpandMore, Refresh, Search, SyncAlt } from "@mui/icons-material"
import { Accordion, AccordionDetails, AccordionSummary, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, InputLabel, Link, Typography } from "@mui/material"
import { useContext, useState } from "react"
import { useForm } from "react-hook-form"
import { Accessibility, AlertDialog, CustomFormField, ReadTable } from "../../component/component"
import { accountTransfer, fetchRegistrationList, getAccountDetails } from "../../service/Service"
import { AppContext } from "../../store/AppProvider"
import { SHOW_ERROR_ALERT, SHOW_INFO_ALERT, SHOW_WARNING_ALERT } from "../../store/reducer/AlertNotifierReducer"
import { ErrorConstant, accountKeys, registrationDetailsMinHeader } from "../../utils/Constants"
import { FEATURES, isEmptyObj } from "../../utils/Utils"

export const TransferAccounts = () => {
    const { setShowLoader, setAlert } = useContext(AppContext)
    const {
        control,
        reset,
        handleSubmit,
        getValues,
        formState: { errors, isDirty, isValid }
    } = useForm({
        defaultValues: {
            from_email: '',
            to_email: '',
        }
    })

    const [showConfirmation, setShowConfirmation] = useState(false)
    const [showRegTable, setShowRegTable] = useState(false)
    const [registrationDetails, setRegistrationDetails] = useState([])
    const [accountDetails, setAccountDetails] = useState({
        fromAccountDetails: {},
        toAccountDetails: {}
    })
    const { fromAccountDetails, toAccountDetails } = accountDetails

    const [selectedEmail, setSelectedEmail] = useState('')
    const [showTransferDetails, setShowTransferDetails] = useState({})

    const getRegistrationDetails = async (email) => {
        try {
            setShowLoader(true)
            setSelectedEmail(email)
            const fetchedData = await fetchRegistrationList({
                email,
                pageIndex: 0,
                pageSize: 100
            })
            if (fetchedData?.data) {
                setRegistrationDetails([...fetchedData?.data])
                setShowRegTable(true)
            }
        } catch (error) {
            setRegistrationDetails([])
            setAlert({ type: SHOW_ERROR_ALERT, alertMessage: ErrorConstant.fetchRegisteredProducts })
        } finally {
            setShowLoader(false)
        }
    }

    const renderFields = (key, obj) => {
        if (key === 'registration_details') {
            return (
                <Link
                    underline="hover"
                    component="button"
                    variant="body2"
                    onClick={() => getRegistrationDetails(obj?.['email'])}
                >
                    Registration Details
                </Link>
            )
        }
        return <Typography variant="subtitle1" >{obj?.[key]}</Typography>
    }

    const handleSearch = async (formData) => {
        try {
            setShowLoader(true)
            if (!isEmptyObj(showTransferDetails)) {
                setShowTransferDetails({})
            }
            if (formData?.from_email?.trim()?.toLowerCase() === formData?.to_email?.trim()?.toLowerCase()) {
                setAlert({ type: SHOW_WARNING_ALERT, alertMessage: 'Please add different email to search!' })
                return
            }
            if (!isEmptyObj(fromAccountDetails) || !isEmptyObj(toAccountDetails)) {
                setAccountDetails({ fromAccountDetails: {}, toAccountDetails: {} })
            }
            const accountDetailsData = await getAccountDetails({ ...formData })
            const { from_accountDetails, to_accountDetails } = accountDetailsData
            if (!isEmptyObj(from_accountDetails) && !isEmptyObj(to_accountDetails)) {
                setAccountDetails({
                    fromAccountDetails: from_accountDetails,
                    toAccountDetails: to_accountDetails,
                })
            } else {
                setAlert({ type: SHOW_INFO_ALERT, alertMessage: ErrorConstant.noAccountDetails })
            }
        } catch (error) {
            setAlert({ type: SHOW_ERROR_ALERT, alertMessage: ErrorConstant.fetchAccountDetails })
        } finally {
            setShowLoader(false)
        }
    }

    const onError = () => { }

    const initiateTransferRequest = async (reason) => {
        try {
            setShowLoader(true)
            const payload = getValues()
            const data = await accountTransfer({ ...payload, reason })
            if (Boolean(data)) {
                setShowConfirmation(false)
                reset()
                setShowTransferDetails({ to_account: payload?.to_email })
            }
        } catch (error) {
            setAlert({ type: SHOW_ERROR_ALERT, alertMessage: ErrorConstant.accountTransfer })
        } finally {
            setShowLoader(false)
        }
    }

    const closeRegDetailsTable = () => {
        setRegistrationDetails([]);
        setSelectedEmail('')
        setShowRegTable(false)

    }

    const isDisabledTransfer = (isEmptyObj(fromAccountDetails) || isEmptyObj(toAccountDetails))

    return (
        <div>
            <div className='header-title' style={{ letterSpacing: '0.5px' }}>{('Transfer Accounts').toUpperCase()}</div>
            <form
                onSubmit={handleSubmit(handleSearch, onError)}
                noValidate
                autoComplete="off">
                <Grid container columnSpacing={2} marginBottom={3} alignItems={'center'} height={90}>
                    <Grid item xs={12} md={4} height={90}>
                        <CustomFormField
                            label="From Account"
                            placeholder="abc@gmail.co"
                            control={control}
                            name="from_email"
                            errorMessage={errors?.from_email?.message}
                            rules={{
                                required: `From Account is required`,
                                pattern: {
                                    value: /\S+@\S+\.\S+/,
                                    message: "Entered value does not match email format",
                                }
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} display={'flex'} md={1} alignItems="center" justifyContent="center" height={90}>
                        <ArrowRightAlt fontSize="large" />
                    </Grid>
                    <Grid item xs={12} md={4} height={90}>
                        <CustomFormField
                            label="To Account"
                            placeholder="xyz@gmail.co"
                            control={control}
                            name="to_email"
                            errorMessage={errors?.to_email?.message}
                            rules={{
                                required: `From Account is required`,
                                pattern: {
                                    value: /\S+@\S+\.\S+/,
                                    message: "Entered value does not match email format",
                                }
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={1.5} height={90} display={'flex'} alignItems={'center'}>
                        <Button startIcon={<Search />} disabled={!isDirty || !isValid} type="submit" variant="contained" size="medium" style={{ width: '100%' }}>Search</Button >
                    </Grid>
                    <Grid item xs={12} md={1.5} height={90} display={'flex'} alignItems={'center'}>
                        <Button startIcon={<Refresh />} variant="outlined" size="medium" style={{ width: '100%', backgroundColor: 'white' }} onClick={() => reset()}>Reset</Button >
                    </Grid>
                </Grid>
            </form>
            {!isEmptyObj(showTransferDetails)
                ? <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMore sx={{ color: 'white', fontSize: 30 }} />} sx={{ backgroundColor: '#408140', maxHeight: 38, '&.Mui-expanded': { backgroundColor: '#408140', minHeight: 48 } }}>
                        <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, fontSize: '1rem' }}>{('Account Transferred').toUpperCase()}</Typography>
                    </AccordionSummary>
                    <AccordionDetails style={{ padding: '10px 15px' }}>
                        <Grid container spacing={2} marginBottom={1}>
                            <Grid item xs={12} md={4}><strong>Account Transferred to </strong></Grid>
                            <Grid item xs={12} md={8}><Typography>{showTransferDetails?.to_account}</Typography></Grid>
                        </Grid>
                        <Grid container spacing={2} marginBottom={1}>
                            <Grid item xs={12} md={4}><strong>Registration Details </strong></Grid>
                            <Grid item xs={12} md={8}>
                                <Link
                                    underline="hover"
                                    component="button"
                                    variant="body2"
                                    onClick={() => getRegistrationDetails(showTransferDetails?.to_account)}
                                >
                                    Registration Details
                                </Link></Grid>
                        </Grid>
                    </AccordionDetails>
                </Accordion>
                : !(isEmptyObj(fromAccountDetails) && isEmptyObj(toAccountDetails))
                    ? <Accordion defaultExpanded>
                        <AccordionSummary expandIcon={<ExpandMore sx={{ color: 'white', fontSize: 30 }} />} sx={{ backgroundColor: '#1976d2', maxHeight: 38, '&.Mui-expanded': { backgroundColor: '#1976d2', minHeight: 48 } }}>
                            <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, fontSize: '1rem' }}>{('Account details').toUpperCase()}</Typography>
                        </AccordionSummary>
                        <AccordionDetails style={{ marginTop: 20, padding: '10px 15px' }}>
                            <Grid container spacing={2} marginBottom={1}>
                                <Grid item xs={12} sm={4} />
                                <Grid item xs={12} sm={4}><h5>From Account</h5></Grid>
                                <Grid item xs={12} sm={4}><h5>To Account</h5></Grid>
                            </Grid>
                            {accountKeys?.map(({ key, value }, index) =>
                                <div style={{ marginTop: 10 }} key={index + key}>
                                    <Grid container spacing={2} marginBottom={1}>
                                        <Grid item xs={12} sm={4}>
                                            <InputLabel>{value}</InputLabel>
                                        </Grid>
                                        <Grid item xs={12} sm={4}>{renderFields(key, fromAccountDetails)}</Grid>
                                        <Grid item xs={12} sm={4}>{renderFields(key, toAccountDetails)}</Grid>
                                    </Grid>
                                    <Divider />
                                </div>)}
                            <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end' }}>
                                <Accessibility feature={FEATURES.TRANSFER_ACCOUNT}>
                                    <Button variant="contained" color="primary" disabled={isDisabledTransfer} onClick={() => setShowConfirmation(true)} startIcon={<SyncAlt />}>TRANSFER</Button>
                                </Accessibility>
                            </div>
                        </AccordionDetails>
                    </Accordion>
                    : <div />}
            {showRegTable
                ? <Dialog
                    fullWidth
                    maxWidth={'lg'}
                    open={Boolean(selectedEmail)}
                    onClose={() => closeRegDetailsTable()}
                >
                    <DialogTitle>Registration Details for Account <strong style={{ color: '#1976d2' }}>{selectedEmail}</strong></DialogTitle>
                    <DialogContent>
                        <ReadTable
                            headers={[...registrationDetailsMinHeader]}
                            data={[...registrationDetails]} />
                    </DialogContent>
                    <DialogActions>
                        <Button variant="contained" onClick={() => closeRegDetailsTable()}>
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>
                : <div />}
            <AlertDialog
                title='Transfer Confirmation'
                description={
                    <>
                        Are you sure you want to Transfer this record?
                        <div style={{ marginTop: 10, marginBottom: 10, display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
                            <Typography color='#1976d2' style={{ fontWeight: 600 }}>{fromAccountDetails?.email}</Typography>
                            &nbsp;<ArrowRightAlt />&nbsp;
                            <Typography color='#1976d2' style={{ fontWeight: 600 }}>{toAccountDetails?.email}</Typography>
                        </div>
                    </>
                }
                show={showConfirmation}
                reasonLabel="Reason*"
                handleConfirmation={(reason) => initiateTransferRequest(reason)}
                handleCancel={() => setShowConfirmation(false)} />
        </div>
    )
}